import React, { useState } from 'react';
import {
  Settings,
  MessageCircle,
  Bell,
  ThumbsUp,
  Reply,
  X,
  Truck,
  Check,
} from 'lucide-react';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TutorialEngine } from '@/components/tutorial/TutorialEngine';
import { cn } from '@/lib/utils';

const COMPANY_NAME = 'RUTAPP';

const PROFILES = [
  { id: 'SARA', own: true, hasWhatsApp: false },
  { id: 'ANA', own: false, hasWhatsApp: true },
  { id: 'DANI', own: false, hasWhatsApp: false },
  { id: 'MANU', own: false, hasWhatsApp: false },
];

const STATUS_KEYS = ['go', 'salimos', 'volvemos', 'fin'];

const STATUS_META = {
  go: { label: 'GO', bg: 'rgba(0, 200, 0, 0.25)', solid: '#00c800' },
  salimos: { label: 'Salimos', bg: 'rgba(0, 123, 255, 0.25)', solid: '#007bff' },
  volvemos: { label: 'Volvemos', bg: 'rgba(255, 215, 0, 0.25)', solid: '#e0be00' },
  fin: { label: 'Fin', bg: 'rgba(255, 0, 0, 0.25)', solid: '#ff0000' },
};

const STEPS = [
  {
    id: 'status-overview',
    message:
      'El color de cada perfil te permite saber de un vistazo en qué estado está cada miembro del equipo. SARA es tu perfil.',
  },
  {
    id: 'admin-info',
    message:
      'Los usuarios ven el estado del equipo, pero cada usuario gestiona únicamente el estado de su propio perfil. El administrador puede personalizar los nombres y colores de los estados, y Rutapp puede trabajar con entre 2 y 4 perfiles.',
  },
  { id: 'status-salimos', message: 'Pulsa Salimos para cambiar el estado de tu perfil.' },
  { id: 'status-volvemos', message: 'Ahora pulsa Volvemos.' },
  { id: 'status-fin', message: 'Por último, pulsa Fin.' },
  {
    id: 'status-result',
    message: 'Cada cambio de estado de tu perfil es visible para el resto del equipo.',
  },
  {
    id: 'whatsapp-action',
    message:
      'Si otro perfil tiene un teléfono configurado, aparecerá aquí el acceso directo a WhatsApp. Pulsa el botón de WhatsApp de ANA para abrir la conversación.',
  },
  {
    id: 'whatsapp-result',
    message: 'Perfecto. En Rutapp, este botón abriría directamente la conversación de WhatsApp con ANA.',
  },
  { id: 'alert-action', message: 'Pulsa la campana para enviar un aviso a todo el equipo.' },
  { id: 'alert-compose', message: 'Escribe un mensaje y pulsa Enviar.' },
  {
    id: 'alert-sent-result',
    message: 'Tu aviso llega así al resto del equipo. Quien lo recibe puede confirmar su lectura o responderte.',
  },
  { id: 'alert-incoming-ok', message: 'Pulsa OK para indicar a ANA que has recibido el aviso.' },
  { id: 'alert-ok-result', message: 'ANA recibe la confirmación de que SARA ha leído el aviso.' },
  {
    id: 'alert-incoming-reply',
    message: 'También puedes responder directamente a quien te envió el aviso.',
  },
  { id: 'alert-reply-compose', message: 'Escribe tu respuesta y pulsa Enviar.' },
  { id: 'alert-reply-result', message: 'Respuesta enviada. DANI recibe tu mensaje.' },
  { id: 'tutorial-complete', message: '' },
];

const CONTINUE_STEPS = new Set([
  'status-overview',
  'admin-info',
  'status-result',
  'whatsapp-result',
  'alert-sent-result',
  'alert-ok-result',
  'alert-reply-result',
]);

function getLocalDateLabel() {
  const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  return `${weekdays[now.getDay()]} · ${dd}/${mm}/${yy}`;
}

function CoachMark({ text }) {
  if (!text) return null;
  return (
    <div className="flex justify-center">
      <div className="max-w-[320px] rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-medium text-foreground shadow-sm">
        {text}
      </div>
    </div>
  );
}

function ContinueButton({ onClick }) {
  return (
    <div className="flex justify-center">
      <Button type="button" size="sm" onClick={onClick}>
        Continuar
      </Button>
    </div>
  );
}

function ProfileChip({ id, statusKey, isActive, onWhatsApp, whatsAppHalo }) {
  const meta = STATUS_META[statusKey];
  const own = id === 'SARA';
  const hasWhatsApp = id === 'ANA';

  return (
    <div className="relative flex flex-col items-center">
      <div
        className={cn(
          'flex flex-col items-center gap-1 p-1 transition-transform',
          isActive ? 'scale-110' : 'scale-100'
        )}
      >
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full border-2"
          style={{ backgroundColor: meta.bg, borderColor: isActive ? meta.solid : 'transparent' }}
        >
          <Truck className="h-7 w-7" style={{ color: meta.solid }} strokeWidth={2} />
        </div>
        <span className="text-xs font-medium text-slate-900">{id}</span>
      </div>

      {own && (
        <span className="mt-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-semibold leading-none text-primary-foreground">
          TU PERFIL
        </span>
      )}

      {own && (
        <div
          className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full border border-primary bg-white"
          aria-label={`Configuración de ${id}`}
          title={`Configuración de ${id}`}
        >
          <Settings className="h-3.5 w-3.5 text-primary" />
        </div>
      )}

      {hasWhatsApp && (
        <button
          type="button"
          onClick={onWhatsApp}
          aria-label={`WhatsApp: ${id}`}
          title={`WhatsApp: ${id}`}
          className={cn(
            'absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#5FCB87] hover:bg-[#4FA575] transition-colors',
            whatsAppHalo && 'ring-4 ring-amber-400 ring-offset-2'
          )}
        >
          <MessageCircle className="h-3.5 w-3.5 text-white" />
        </button>
      )}
    </div>
  );
}

function BannerCard({ kind, message, sender, children }) {
  const isConfirmation = kind === 'confirmation';
  const Icon = isConfirmation ? ThumbsUp : Bell;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border shadow-md',
        isConfirmation ? 'border-green-300 bg-green-50' : 'border-yellow-300 bg-yellow-50'
      )}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Icon
            className={cn('h-5 w-5 flex-shrink-0', isConfirmation ? 'text-green-600' : 'text-yellow-600')}
          />
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'break-words text-sm font-medium',
                isConfirmation ? 'text-green-900' : 'text-yellow-900'
              )}
            >
              {message}
            </p>
            {sender && (
              <p className={cn('mt-1 text-xs', isConfirmation ? 'text-green-800' : 'text-yellow-800')}>
                Por: {sender}
              </p>
            )}
          </div>
        </div>
        {children && <div className="flex items-center gap-2">{children}</div>}
      </div>
    </div>
  );
}

function BannerAction({ icon: Icon, label, colorClass, halo, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full p-0',
        colorClass,
        halo && 'ring-4 ring-amber-400 ring-offset-2'
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function SimulatedComplete({ onRestartTutorial, onExitTutorial, exitLabel = 'Volver a Tutoriales' }) {
  return (
    <div className="rounded-lg border bg-background p-6 text-center">
      <h3 className="text-lg font-semibold">Has completado Comunicación entre usuarios.</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Ya sabes consultar el estado del equipo, comunicarte con otros perfiles y gestionar avisos.
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button type="button" variant="outline" onClick={onRestartTutorial}>
          Repetir tutorial
        </Button>
        <Button type="button" onClick={onExitTutorial}>
          {exitLabel}
        </Button>
      </div>
    </div>
  );
}

export function TeamCommunicationTutorial({ onExit, completionActionLabel, onCompletionAction }) {
  const [activeProfile, setActiveProfile] = useState('SARA');
  const [profileStatuses, setProfileStatuses] = useState({
    SARA: 'go',
    ANA: 'salimos',
    DANI: 'volvemos',
    MANU: 'fin',
  });

  const [alertComposerOpen, setAlertComposerOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [alertAcknowledged, setAlertAcknowledged] = useState(false);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySent, setReplySent] = useState(false);

  const resetScenario = () => {
    setActiveProfile('SARA');
    setProfileStatuses({ SARA: 'go', ANA: 'salimos', DANI: 'volvemos', MANU: 'fin' });
    setAlertComposerOpen(false);
    setAlertText('');
    setAlertAcknowledged(false);
    setReplyOpen(false);
    setReplyText('');
    setReplySent(false);
  };

  const handleBack = (targetStep) => {
    if (!targetStep) return;

    switch (targetStep.id) {
      case 'status-salimos':
        setProfileStatuses((prev) => ({ ...prev, SARA: 'go' }));
        break;
      case 'status-volvemos':
        setProfileStatuses((prev) => ({ ...prev, SARA: 'salimos' }));
        break;
      case 'status-fin':
        setProfileStatuses((prev) => ({ ...prev, SARA: 'volvemos' }));
        break;
      case 'alert-action':
        setAlertComposerOpen(false);
        setAlertText('');
        break;
      case 'alert-compose':
        setAlertComposerOpen(true);
        setAlertText('');
        break;
      case 'alert-incoming-ok':
        setAlertAcknowledged(false);
        break;
      case 'alert-incoming-reply':
        setReplyOpen(false);
        setReplyText('');
        break;
      case 'alert-reply-compose':
        setReplyOpen(true);
        setReplyText('');
        break;
      default:
        break;
    }
  };

  const renderStep = (step, index, actions) => {
    const goNext = actions?.goNext ?? (() => {});
    const restart = actions?.restart ?? (() => {});
    const exit = actions?.exit ?? (() => {});
    const completeAction = onCompletionAction ?? exit;
    const completeLabel = completionActionLabel ?? 'Volver a Tutoriales';

    const handleContinue = () => goNext();

    const handleStatusClick = (statusKey) => {
      const expected = {
        'status-salimos': 'salimos',
        'status-volvemos': 'volvemos',
        'status-fin': 'fin',
      }[step.id];
      if (!expected || statusKey !== expected) return;
      setProfileStatuses((prev) => ({ ...prev, SARA: statusKey }));
      goNext();
    };

    const handleWhatsAppClick = () => {
      if (step.id !== 'whatsapp-action') return;
      goNext();
    };

    const handleBellClick = () => {
      if (step.id === 'alert-action') {
        setAlertComposerOpen(true);
        goNext();
      } else if (step.id === 'alert-compose') {
        setAlertComposerOpen(true);
      }
    };

    const handleAlertSubmit = () => {
      if (step.id !== 'alert-compose') return;
      if (!alertText.trim()) return;
      setAlertComposerOpen(false);
      goNext();
    };

    const handleAlertCancel = () => {
      setAlertText('');
      setAlertComposerOpen(false);
    };

    const handleOkClick = () => {
      if (step.id !== 'alert-incoming-ok') return;
      setAlertAcknowledged(true);
      goNext();
    };

    const handleReplyClick = () => {
      if (step.id !== 'alert-incoming-reply') return;
      setReplyOpen(true);
      goNext();
    };

    const handleReplySubmit = () => {
      if (step.id !== 'alert-reply-compose') return;
      if (!replyText.trim()) return;
      setReplySent(true);
      setReplyOpen(false);
      goNext();
    };

    const handleReplyCancel = () => {
      setReplyText('');
      setReplyOpen(false);
    };

    if (step.id === 'tutorial-complete') {
      return <SimulatedComplete onRestartTutorial={restart} onExitTutorial={completeAction} exitLabel={completeLabel} />;
    }

    const haloStatus =
      {
        'status-salimos': 'salimos',
        'status-volvemos': 'volvemos',
        'status-fin': 'fin',
      }[step.id] || null;
    const whatsAppHalo = step.id === 'whatsapp-action';
    const bellHalo =
      step.id === 'alert-action' || (step.id === 'alert-compose' && !alertComposerOpen);

    const isContinue = CONTINUE_STEPS.has(step.id);

    const mainScreen = (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="relative flex items-center justify-between border-b border-slate-200 pb-3">
          <div className="h-10 w-10" />
          <div className="text-xl font-bold text-slate-900">{COMPANY_NAME}</div>
          <button
            type="button"
            onClick={handleBellClick}
            aria-label="Abrir avisos entre perfiles"
            className={cn(
              'flex h-10 w-10 items-center justify-center border-0 bg-transparent',
              bellHalo && 'rounded-full ring-4 ring-amber-400 ring-offset-2'
            )}
          >
            <Bell className="h-7 w-7" stroke="#000000" fill="#fff191" strokeWidth={0.8} />
          </button>
        </div>

        <div className="mt-4 flex items-start justify-center gap-3">
          {PROFILES.map((p) => (
            <ProfileChip
              key={p.id}
              id={p.id}
              statusKey={profileStatuses[p.id]}
              isActive={activeProfile === p.id}
              onWhatsApp={handleWhatsAppClick}
              whatsAppHalo={whatsAppHalo}
            />
          ))}
        </div>

        <div className="mt-5 flex justify-center gap-2">
          {STATUS_KEYS.map((key) => {
            const meta = STATUS_META[key];
            const isActive = profileStatuses[activeProfile] === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleStatusClick(key)}
                className={cn(
                  'flex-1 rounded-full border border-primary bg-card px-3 py-1.5 text-sm font-medium text-foreground',
                  haloStatus === key && 'border-amber-400 ring-4 ring-amber-400 ring-offset-2'
                )}
                style={isActive ? { backgroundColor: meta.bg } : undefined}
              >
                {meta.label}
              </button>
            );
          })}
        </div>

        <div className="mt-3 text-center text-xs text-slate-500">{getLocalDateLabel()}</div>
      </div>
    );

    const composeModal = (
      <div className="rounded-lg border bg-background shadow-lg">
        <div className="border-b px-4 py-3 text-center">
          <h3 className="text-base font-semibold">Mandar aviso a otros perfiles</h3>
        </div>
        <div className="space-y-3 p-4">
          <Textarea
            value={alertText}
            onChange={(e) => setAlertText(e.target.value)}
            placeholder="Escribe un mensaje para los demás perfiles..."
            maxLength={160}
            rows={4}
            className={cn(
              'resize-none',
              alertText.trim() === '' && 'border-amber-400 ring-4 ring-amber-400 ring-offset-2'
            )}
          />
          <div className="text-right text-xs text-muted-foreground">
            {alertText.length}/160 caracteres
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleAlertCancel}>
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAlertSubmit}
              disabled={alertText.trim() === ''}
              className={cn(
                'bg-blue-600 hover:bg-blue-700',
                alertText.trim() !== '' && 'ring-4 ring-amber-400 ring-offset-2'
              )}
            >
              Enviar
            </Button>
          </div>
        </div>
      </div>
    );

    const replyModal = (
      <div className="rounded-lg border bg-background shadow-lg">
        <div className="border-b px-4 py-3 text-center">
          <h3 className="text-base font-semibold">Mandar aviso a otros perfiles</h3>
        </div>
        <div className="space-y-3 p-4">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Escribe un mensaje para los demás perfiles..."
            maxLength={160}
            rows={4}
            className={cn(
              'resize-none',
              replyText.trim() === '' && 'border-amber-400 ring-4 ring-amber-400 ring-offset-2'
            )}
          />
          <div className="text-right text-xs text-muted-foreground">
            {replyText.length}/160 caracteres
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleReplyCancel}>
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleReplySubmit}
              disabled={replyText.trim() === ''}
              className={cn(
                'bg-blue-600 hover:bg-blue-700',
                replyText.trim() !== '' && 'ring-4 ring-amber-400 ring-offset-2'
              )}
            >
              Enviar
            </Button>
          </div>
        </div>
      </div>
    );

    if (step.id === 'alert-compose') {
      if (alertComposerOpen) {
        return (
          <div className="space-y-3">
            {composeModal}
            <CoachMark text={step.message} />
          </div>
        );
      }
      return (
        <div className="space-y-3">
          {mainScreen}
          <CoachMark text="Pulsa la campana para abrir el aviso." />
        </div>
      );
    }

    if (step.id === 'alert-reply-compose') {
      if (replyOpen) {
        return (
          <div className="space-y-3">
            {replyModal}
            <CoachMark text={step.message} />
          </div>
        );
      }
      return (
        <div className="space-y-3">
          <BannerCard kind="incoming" message="¿Volvemos ya?" sender="DANI">
            <BannerAction
              icon={Reply}
              label="Responder aviso"
              colorClass="bg-blue-100 text-blue-600"
              halo
              onClick={() => setReplyOpen(true)}
            />
          </BannerCard>
          <CoachMark text="Pulsa Responder para abrir el modal de respuesta." />
        </div>
      );
    }

    if (step.id === 'alert-sent-result') {
      return (
        <div className="space-y-3">
          <BannerCard kind="incoming" message={alertText.trim() || 'Hola'} sender="SARA" />
          <CoachMark text={step.message} />
          <ContinueButton onClick={handleContinue} />
        </div>
      );
    }

    if (step.id === 'alert-incoming-ok') {
      return (
        <div className="space-y-3">
          <BannerCard kind="incoming" message="¿Todo bien?" sender="ANA">
            <BannerAction
              icon={ThumbsUp}
              label="Confirmar lectura del aviso"
              colorClass="bg-green-100 text-green-700"
              halo
              onClick={handleOkClick}
            />
            <BannerAction
              icon={Reply}
              label="Responder aviso"
              colorClass="bg-blue-100 text-blue-600"
              onClick={() => {}}
            />
            <BannerAction
              icon={X}
              label="Cerrar aviso"
              colorClass="bg-stone-200 text-stone-600"
              onClick={() => {}}
            />
          </BannerCard>
          <CoachMark text={step.message} />
        </div>
      );
    }

    if (step.id === 'alert-ok-result') {
      return (
        <div className="space-y-3">
          <BannerCard kind="confirmation" message="SARA confirma la lectura del aviso" />
          <CoachMark text={step.message} />
          <ContinueButton onClick={handleContinue} />
        </div>
      );
    }

    if (step.id === 'alert-incoming-reply') {
      return (
        <div className="space-y-3">
          <BannerCard kind="incoming" message="¿Volvemos ya?" sender="DANI">
            <BannerAction
              icon={ThumbsUp}
              label="Confirmar lectura del aviso"
              colorClass="bg-green-100 text-green-700"
              onClick={() => {}}
            />
            <BannerAction
              icon={Reply}
              label="Responder aviso"
              colorClass="bg-blue-100 text-blue-600"
              halo
              onClick={handleReplyClick}
            />
            <BannerAction
              icon={X}
              label="Cerrar aviso"
              colorClass="bg-stone-200 text-stone-600"
              onClick={() => {}}
            />
          </BannerCard>
          <CoachMark text={step.message} />
        </div>
      );
    }

    if (step.id === 'alert-reply-result') {
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3">
            <Check className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">Respuesta enviada</span>
          </div>
          {replyText.trim() && (
            <div className="rounded-md border bg-card px-3 py-2 text-center text-sm text-foreground">
              {replyText.trim()}
            </div>
          )}
          <CoachMark text={step.message} />
          <ContinueButton onClick={handleContinue} />
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {mainScreen}
        <CoachMark text={step.message} />
        {isContinue && <ContinueButton onClick={handleContinue} />}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Comunicación entre usuarios</DialogTitle>
        <DialogDescription>
          Consulta estados, comunícate con el equipo y gestiona avisos en un entorno de práctica.
        </DialogDescription>
      </DialogHeader>

      <TutorialEngine
        steps={STEPS}
        renderStep={renderStep}
        onExit={onExit}
        onRestart={resetScenario}
        onBack={handleBack}
        showStepNavigation={false}
      />
    </div>
  );
}
